/**
 * Contexto de autenticación del cliente.
 *
 * Define la forma del contexto y lo crea; el `AuthProvider` (en
 * `AuthProvider.tsx`) lo puebla y el hook `useAuth` (en `useAuth.ts`) lo
 * consume. Se separa la definición del provider para que `useAuth` no arrastre
 * el árbol del provider y para mantener un archivo por responsabilidad
 * (`docs/frontend_conventions.md`).
 *
 * El estado de auth (user + permisos + sede activa) es **estado de UI**, no dato
 * de servidor cacheado por query key: vive en Context (`docs/frontend_
 * architecture.md`, Pr. 4). La fuente de verdad se hidrata desde `GET /auth/me`
 * (cookie httpOnly); aquí nunca se guarda token ni credencial (Pr. 3).
 */
import { createContext } from "react";

import type { ActiveSede, AuthUser } from "@/features/auth/types";

/** Fase del ciclo de autenticación, derivada de la hidratación de `/auth/me`. */
export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

/** API pública del contexto de auth (lo que expone `useAuth`). */
export interface AuthContextValue {
  status: AuthStatus;
  /** Usuario autenticado, o `null` mientras carga / si no hay sesión. */
  user: AuthUser | null;
  /** Permisos del usuario (lista vacía si no hay sesión). */
  permissions: string[];
  /** Sede activa resuelta por el backend, o `null` (multi-sede sin elegir). */
  activeSede: ActiveSede | null;
  /**
   * Indica si el usuario opera sobre TODAS las sedes (multi-sede). Misma
   * semántica que el backend (`_user_has_all_sedes_access`): rol `admin` o
   * permiso `all`. Determina si se muestra el selector de sede.
   */
  isMultiSede: boolean;
  /**
   * `true` si la sede activa tiene activo el módulo de inventario. Espejo de
   * `sede_inv_activo` del SSR; gobierna la visibilidad de la sección Inventario.
   * `false` mientras el backend de `/auth/me` no emita el flag.
   */
  sedeInvActivo: boolean;
  /**
   * `true` si la sede activa tiene activo el horario láser. Espejo de
   * `sede_laser_activo` del SSR; gobierna la visibilidad de Horario láser.
   */
  sedeLaserActivo: boolean;
  /** `true` si el usuario tiene el permiso (o `all`). */
  hasPermission: (permission: string) => boolean;
  /** Fija la sede activa (id del store `lib/sede`) y revalida la identidad. */
  setActiveSede: (sedeId: number) => void;
  /** Cierra sesión en el backend y limpia el estado del cliente. */
  logout: () => Promise<void>;
}

/**
 * Contexto sin valor por defecto: `useAuth` exige estar dentro del
 * `AuthProvider` (lanza si no), evitando un estado silenciosamente incorrecto.
 */
export const AuthContext = createContext<AuthContextValue | null>(null);
